/**
 * web/src/hooks/mutations/useSkipGate.ts
 * Optimistic skip — same shape as useApproveGate. Removes the gate from the
 * pending list immediately, fires success toast, rolls back on error.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { skipGate, type SkipGateBody } from '../../api/gates';
import { gateOptimisticHandlers, type GateMutationVars } from './_optimistic';

/** Variables for the skip mutation. */
export type UseSkipGateVars = GateMutationVars;

/**
 * Skips a gate optimistically. Used when the reviewer sent the email
 * outside the system and just wants the workflow to advance. Shares the
 * optimistic + reconciliation handlers with useApproveGate.
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP POST; cache writes on the pending-gates list
 * @idempotent no — server returns 409 on a re-skip against a no-longer-waiting step
 */
export function useSkipGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: UseSkipGateVars) => {
      const body: SkipGateBody = { edits: vars.edits };
      return skipGate(vars.workflowId, vars.stepIndex, body);
    },
    ...gateOptimisticHandlers(qc, {
      defaultSuccess: 'Skipped — workflow resumed',
      errorFallback: 'Skip failed — please retry',
    }),
  });
}
